import type { ReactElement, ReactNode } from "react";
import React from "react";
import { Alert } from "@mui/material";
import AlertTitle from "@mui/material/AlertTitle";
import Check from "@/assets/svgs/check.svg";
import Warning from "@/assets/svgs/warning.svg";
import InfoSolid from "@/assets/svgs/info-solid.svg";
import type {
  SVG,
  StyledComponentBase,
} from "@/components/component-base.types";
import type { MessageBoxType } from "@/types/shared.types";
import classNames from "classnames";
import styles from "./message-box.module.scss";

export interface MessageBoxProps extends StyledComponentBase {
  /**
   * Title for the MessageBox. Could be a string, HTML, or any existing component.
   */
  title?: string | ReactElement;
  /** MessageBoxType determines coloring and icons. */
  messageBoxType?: MessageBoxType;
  /** The contents of the MessageBox. Could be a string, HTML, or any existing component. */
  children: string | ReactElement | ReactNode;
  /**
   * The icon to display. If no icon is specified, a default icon will be used,
   * corresponding to the MessageBoxType. To remove the icon, set icon to false.
   */
  // icon?: SVG | false;
  icon?: SVG | false;
}

export const iconMap = {
  highlight: false,
  informative: InfoSolid,
  success: Check,
  warning: Warning,
  critical: Warning,
};

export const MessageBox = (iProps: MessageBoxProps) => {
  const { title, messageBoxType = "highlight", className, children } = iProps;

  // if icon was passed in, use it. Otherwise, take the default icon.
  const { icon = iconMap[messageBoxType] } = iProps;

  // icon will either be SVG or false at this point.
  let theIcon = icon as ReactElement | false;
  // if not false, cast it as an svg element with appropriate styling based on messageBoxType.
  if (icon) {
    const Svg = icon as SVG;
    theIcon = <Svg className={styles[`${messageBoxType}Icon`]} />;
  }

  return (
    <Alert
      severity={undefined}
      variant={messageBoxType}
      icon={theIcon as ReactNode}
      className={classNames(className, styles[messageBoxType])}
    >
      {title && (
        <AlertTitle>
          <span className="u-bold">{title}</span>
        </AlertTitle>
      )}
      {children}
    </Alert>
  );
};
